/** Granular RBAC master module keys on `companies/{id}.activeModules`. */
export const MASTER_MODULE_KEYS = [
  'ticketing',
  'portfolio',
  'monitoring',
  'agile',
  'externalLinks',
  'codeReview',
  'chatWidget',
  'aiGameStudio',
  'aiStudio',
  'reports',
  'files',
  'vault',
  'crm',
  'financeOps',
  'aiInsights',
];

/** @type {Record<string, boolean>} */
export const DEFAULT_MASTER_ACTIVE_MODULES = {
  ticketing: true,
  portfolio: false,
  monitoring: false,
  agile: false,
  externalLinks: true,
  codeReview: true,
  chatWidget: false,
  aiGameStudio: false,
  // Previously role-gated only — keep ON when unset so existing tenants keep access.
  aiStudio: true,
  reports: true,
  files: true,
  vault: false,
  crm: true,
  financeOps: false,
  aiInsights: false,
};

/** @type {Record<string, Record<string, string[]>>} */
export const DEFAULT_ROLE_PERMISSIONS = {
  developer: {
    agile: ['read', 'write'],
    monitoring: ['read', 'write'],
    codeReview: ['read', 'write'],
  },
  manager: {
    agile: ['read', 'write', 'delete'],
    portfolio: ['read'],
    ticketing: ['read', 'write'],
    chatWidget: ['read', 'write'],
  },
  agent: {
    ticketing: ['read', 'write'],
    chatWidget: ['read', 'write'],
  },
};

export const PERMISSION_ACTIONS = ['read', 'write', 'delete'];

/** Modules shown in the simplified Role Permissions Matrix (Users & access). */
export const MATRIX_MODULE_KEYS = ['agile', 'chatWidget', 'ticketing', 'portfolio'];

export const MATRIX_MODULE_LABELS = {
  agile: 'Agile',
  chatWidget: 'Chat',
  ticketing: 'Ticketing',
  portfolio: 'Portfolio',
};

/** Internal roles editable by Company Admin in the permissions matrix. */
export const PERMISSION_MANAGE_ROLES = ['manager', 'developer', 'agent'];

export const PERMISSION_ROLE_LABELS = {
  manager: 'Manager',
  developer: 'Developer',
  agent: 'Agent',
};

export const MASTER_MODULE_LABELS = {
  ticketing: 'Ticketing',
  portfolio: 'Portfolio',
  monitoring: 'Monitoring',
  agile: 'Agile',
  externalLinks: 'External Links',
  codeReview: 'AI code review',
  chatWidget: 'Chat Support Widget',
  aiGameStudio: 'AI Game Studio',
  aiStudio: 'AI Studio',
  reports: 'Reports',
  files: 'Files',
  vault: 'Vault',
  crm: 'CRM / Marketing',
  financeOps: 'Finance ops',
  aiInsights: 'AI insights',
};

export const MASTER_MODULE_DESCRIPTIONS = {
  ticketing: 'Support tickets and customer issue tracking',
  portfolio: 'Product portfolio, infra costs, and AI analysis',
  monitoring: 'Site and app liveness monitoring sweeps',
  agile: 'Sprint planning and kanban boards',
  externalLinks: 'Curated external links for the workspace',
  codeReview: 'Review and approve AI-proposed code patches before deployment',
  chatWidget: 'Embeddable public chat support widget for customer sites',
  aiGameStudio: 'AI-assisted game asset generation and Phaser project scaffolding',
  aiStudio: 'AI Workforce roster, agent prompts, and studio tools',
  reports: 'Enterprise reports hub, including Friction Insights review',
  files: 'Company file browser and storage nodes',
  vault: 'Secure credentials and secrets storage',
  crm: 'CRM campaigns, outreach, and marketing tools',
  financeOps: 'Finance ops overview widgets and cost summaries',
  aiInsights: 'AI insights widgets on the company overview',
};

/** Legacy `moduleRequests` / `activeModules` keys that map to each master module. */
export const MASTER_MODULE_LEGACY_KEYS = {
  ticketing: ['ticketing'],
  portfolio: ['portfolio'],
  monitoring: ['monitoring', 'uptimeMonitoring'],
  agile: ['agile', 'agileKanban'],
  externalLinks: ['externalLinks'],
  codeReview: ['codeReview'],
  chatWidget: ['chatWidget', 'chatSupport'],
  aiGameStudio: ['aiGameStudio'],
  aiStudio: ['aiStudio'],
  reports: ['reports'],
  files: ['files'],
  vault: ['vault'],
  crm: ['crm'],
  financeOps: ['financeOps'],
  aiInsights: ['aiInsights'],
};

/** Roles that may view/edit AI spend + monthly budget. */
export const ADMIN_ROLES = new Set(['super_admin', 'company_admin']);

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function isAdminRole(role) {
  return typeof role === 'string' && ADMIN_ROLES.has(role);
}

/**
 * Resolve master-module flags from workspace (supports legacy activeModules keys).
 * @param {{ activeModules?: Record<string, boolean>, enabledModules?: Record<string, boolean> } | null | undefined} workspace
 * @returns {Record<string, boolean>}
 */
export function resolveMasterActiveModules(workspace) {
  const am = workspace?.activeModules || {};
  const em = workspace?.enabledModules || {};

  const boolOrDefault = (value, fallback) =>
    typeof value === 'boolean' ? value : fallback;

  return {
    ticketing: am.ticketing === true,
    portfolio: am.portfolio === true,
    monitoring: am.monitoring === true || am.uptimeMonitoring === true,
    agile: am.agile === true || am.agileKanban === true,
    externalLinks: am.externalLinks === true || em.externalLinks === true,
    codeReview:
      am.codeReview === true ||
      (am.codeReview === undefined && DEFAULT_MASTER_ACTIVE_MODULES.codeReview),
    chatWidget:
      am.chatWidget === true ||
      am.chatSupport === true,
    aiGameStudio: am.aiGameStudio === true,
    aiStudio: boolOrDefault(am.aiStudio, DEFAULT_MASTER_ACTIVE_MODULES.aiStudio),
    reports: boolOrDefault(am.reports, DEFAULT_MASTER_ACTIVE_MODULES.reports),
    files: boolOrDefault(am.files, DEFAULT_MASTER_ACTIVE_MODULES.files),
    vault: am.vault === true,
    crm: boolOrDefault(am.crm, true),
    financeOps: em.financeOps === true || am.financeOps === true,
    aiInsights: em.aiInsights === true || am.aiInsights === true,
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} raw
 * @returns {Record<string, Record<string, string[]>>}
 */
export function normalizeRolePermissions(raw) {
  const out = {};
  for (const [role, modules] of Object.entries(DEFAULT_ROLE_PERMISSIONS)) {
    out[role] = {};
    for (const [mod, actions] of Object.entries(modules)) {
      out[role][mod] = [...actions];
    }
  }

  if (!raw || typeof raw !== 'object') return out;

  for (const [role, modules] of Object.entries(raw)) {
    if (!modules || typeof modules !== 'object') continue;
    if (!out[role]) out[role] = {};
    for (const [mod, actions] of Object.entries(modules)) {
      if (!Array.isArray(actions)) continue;
      out[role][mod] = actions.filter(
        (a) => typeof a === 'string' && PERMISSION_ACTIONS.includes(a),
      );
    }
  }

  return out;
}

/**
 * @param {string | null | undefined} role
 * @param {{ activeModules?: Record<string, boolean>, enabledModules?: Record<string, boolean>, rolePermissions?: Record<string, Record<string, string[]>> } | null | undefined} workspace
 * @param {string} moduleName
 * @param {string} action
 * @returns {boolean}
 */
export function checkUserPermission(role, workspace, moduleName, action) {
  if (!moduleName || !action) return false;
  if (!PERMISSION_ACTIONS.includes(action)) return false;

  const safeRole = typeof role === 'string' ? role : null;

  // Platform super admins bypass tenant module toggles and the role matrix.
  if (safeRole === 'super_admin') return true;

  const masterActive = resolveMasterActiveModules(workspace);
  if (!masterActive[moduleName]) return false;

  if (safeRole && ADMIN_ROLES.has(safeRole)) return true;

  if (!safeRole) return false;

  const rolePermissions = normalizeRolePermissions(workspace?.rolePermissions);
  const modulePerms = rolePermissions[safeRole]?.[moduleName];
  if (!Array.isArray(modulePerms)) return false;

  return modulePerms.includes(action);
}

/** Count of enabled master modules (for admin tables). */
export function countMasterActiveModules(workspace) {
  const master = resolveMasterActiveModules(workspace);
  return MASTER_MODULE_KEYS.filter((k) => master[k]).length;
}

/**
 * Apply a master-module toggle to Firestore `activeModules` / `enabledModules` payloads.
 * Writes both master and legacy keys where applicable.
 * @param {Record<string, boolean>} activeModules
 * @param {Record<string, boolean>} enabledModules
 * @param {string} masterKey
 * @param {boolean} checked
 */
export function applyMasterModuleToggle(activeModules, enabledModules, masterKey, checked) {
  const nextActive = { ...(activeModules || {}) };
  const nextEnabled = { ...(enabledModules || {}) };

  switch (masterKey) {
    case 'ticketing':
      nextActive.ticketing = checked;
      break;
    case 'portfolio':
      nextActive.portfolio = checked;
      break;
    case 'monitoring':
      nextActive.monitoring = checked;
      nextActive.uptimeMonitoring = checked;
      break;
    case 'agile':
      nextActive.agile = checked;
      nextActive.agileKanban = checked;
      break;
    case 'externalLinks':
      nextActive.externalLinks = checked;
      nextEnabled.externalLinks = checked;
      break;
    case 'codeReview':
      nextActive.codeReview = checked;
      break;
    case 'chatWidget':
      nextActive.chatWidget = checked;
      nextActive.chatSupport = checked;
      break;
    case 'aiGameStudio':
      nextActive.aiGameStudio = checked;
      break;
    case 'aiStudio':
      nextActive.aiStudio = checked;
      break;
    case 'reports':
      nextActive.reports = checked;
      break;
    case 'files':
      nextActive.files = checked;
      break;
    case 'vault':
      nextActive.vault = checked;
      break;
    case 'crm':
      nextActive.crm = checked;
      break;
    case 'financeOps':
      nextActive.financeOps = checked;
      nextEnabled.financeOps = checked;
      break;
    case 'aiInsights':
      nextActive.aiInsights = checked;
      nextEnabled.aiInsights = checked;
      break;
    default:
      break;
  }

  return { activeModules: nextActive, enabledModules: nextEnabled };
}

/**
 * @param {Record<string, Record<string, string[]>>} rolePermissions
 * @param {string} role
 * @param {string} module
 * @param {string} action
 * @param {boolean} checked
 */
export function toggleRolePermission(rolePermissions, role, module, action, checked) {
  const next = normalizeRolePermissions(rolePermissions);
  if (!next[role]) next[role] = {};
  let actions = [...(next[role][module] || [])];

  if (checked) {
    if (!actions.includes(action)) actions.push(action);
    if (action === 'write' && !actions.includes('read')) actions.push('read');
  } else {
    actions = actions.filter((a) => a !== action);
    if (action === 'read') {
      actions = actions.filter((a) => a !== 'write' && a !== 'delete');
    }
  }

  if (actions.length === 0) {
    delete next[role][module];
  } else {
    next[role][module] = actions;
  }

  return next;
}

/** @param {Record<string, Record<string, string[]>>} rolePermissions */
export function hasRolePermission(rolePermissions, role, module, action) {
  const perms = normalizeRolePermissions(rolePermissions);
  return Array.isArray(perms[role]?.[module]) && perms[role][module].includes(action);
}

/** Whether a role has any access to a module (read is sufficient for ON). */
export function isRoleModuleEnabled(rolePermissions, role, module) {
  return hasRolePermission(rolePermissions, role, module, 'read');
}

/**
 * Simplified matrix toggle — ON grants read + write; OFF clears the module.
 * @param {Record<string, Record<string, string[]>>} rolePermissions
 * @param {string} role
 * @param {string} module
 * @param {boolean} enabled
 */
export function toggleRoleModuleEnabled(rolePermissions, role, module, enabled) {
  if (enabled) {
    let next = toggleRolePermission(rolePermissions, role, module, 'read', true);
    next = toggleRolePermission(next, role, module, 'write', true);
    return next;
  }

  const next = normalizeRolePermissions(rolePermissions);
  if (!next[role]) return next;
  const roleMods = { ...next[role] };
  delete roleMods[module];
  if (Object.keys(roleMods).length === 0) {
    delete next[role];
  } else {
    next[role] = roleMods;
  }
  return next;
}

export { canUseRichText, RICH_TEXT_ROLES } from './userRoleHierarchy';
